/**
 * Iter 41.39 — Self-service Change Password screen.
 *
 * Rendered when the authenticated user has `must_change_password=true`
 * (force flow) or when they navigate here voluntarily. NO sidebar / NO
 * topbar — locked layout so the user cannot reach any other UI surface
 * until they rotate the password.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/shared/PasswordInput';
import { Label } from '@/components/ui/label';
import { Lock, KeyRound, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';

const HOME_FOR_ROLE = {
  hm_admin: '/admin', hm_advisor: '/admin', hm_reviewer: '/admin',
  client_admin: '/client', client_hr: '/client', client_viewer: '/client',
  evaluated: '/me',
};

export default function ChangePassword() {
  const { t } = useTranslation();
  const { user, refreshUser, logout } = useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const forced = !!user?.must_change_password;

  // Live strength check (mirrors backend rules; backend is the source of truth)
  const passwordChecks = {
    length:    next.length >= 8,
    upper:     /[A-Z]/.test(next),
    lower:     /[a-z]/.test(next),
    digit:     /\d/.test(next),
    match:     next.length > 0 && next === confirm,
    different: next.length > 0 && next !== current,
  };
  const allOk = Object.values(passwordChecks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allOk) {
      setError(t('changePassword.errors.notAllOk', {
        defaultValue: 'Please satisfy all password requirements.',
      }));
      return;
    }
    setSaving(true);
    try {
      await api.patch('/auth/me/password', {
        current_password: current,
        new_password:     next,
        confirm_password: confirm,
      });
      await refreshUser?.();
      toast.success(t('changePassword.success', { defaultValue: 'Password updated' }));
      navigate(HOME_FOR_ROLE[user?.role] || '/', { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/10 p-4"
      data-testid="change-password-screen"
    >
      <Card className="w-full max-w-md p-8 shadow-card">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-2">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-bold">
            {t('changePassword.title', { defaultValue: 'Update your password' })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {forced
              ? t('changePassword.forcedSubtitle', {
                  defaultValue: 'Your administrator requires a password change before continuing.',
                })
              : t('changePassword.subtitle', {
                  defaultValue: 'Choose a strong new password for your account.',
                })}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cp-current">
              {t('changePassword.current', { defaultValue: 'Current password' })}
            </Label>
            <PasswordInput
              id="cp-current"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="cp-current"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">
              {t('changePassword.new', { defaultValue: 'New password' })}
            </Label>
            <PasswordInput
              id="cp-new"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              data-testid="cp-new"
            />
          </div>
          <div>
            <Label htmlFor="cp-confirm">
              {t('changePassword.confirm', { defaultValue: 'Confirm new password' })}
            </Label>
            <PasswordInput
              id="cp-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              data-testid="cp-confirm"
            />
          </div>

          <ul className="text-xs space-y-1 mt-3" data-testid="cp-rules">
            <Rule ok={passwordChecks.length}    label={t('changePassword.rule.length',    { defaultValue: 'At least 8 characters' })} />
            <Rule ok={passwordChecks.upper}     label={t('changePassword.rule.upper',     { defaultValue: 'One uppercase letter' })} />
            <Rule ok={passwordChecks.lower}     label={t('changePassword.rule.lower',     { defaultValue: 'One lowercase letter' })} />
            <Rule ok={passwordChecks.digit}     label={t('changePassword.rule.digit',     { defaultValue: 'One digit' })} />
            <Rule ok={passwordChecks.match}     label={t('changePassword.rule.match',     { defaultValue: 'New and confirmation match' })} />
            <Rule ok={passwordChecks.different} label={t('changePassword.rule.different', { defaultValue: 'Different from current password' })} />
          </ul>

          {error && (
            <p className="text-sm text-destructive flex items-start gap-1.5" data-testid="cp-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}

          <Button
            type="submit"
            disabled={saving || !allOk}
            className="w-full bg-gradient-primary hover:opacity-90"
            data-testid="cp-submit"
          >
            {saving
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.saving')}</>
              : <><Lock className="h-4 w-4 mr-2" />{t('changePassword.submit', { defaultValue: 'Update password' })}</>}
          </Button>

          {!forced && (
            <button
              type="button"
              onClick={() => navigate(HOME_FOR_ROLE[user?.role] || '/')}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
              data-testid="cp-cancel"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
          )}
          {forced && (
            <button
              type="button"
              onClick={async () => { await logout?.(); navigate('/login'); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
              data-testid="cp-logout"
            >
              {t('changePassword.logout', { defaultValue: 'Sign out instead' })}
            </button>
          )}
        </form>
      </Card>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? 'text-success' : 'text-muted-foreground'}`}>
      {ok
        ? <CheckCircle2 className="h-3.5 w-3.5" />
        : <span className="inline-block h-3.5 w-3.5 rounded-full border" />}
      {label}
    </li>
  );
}
